import slugify from "slugify";

import { IBaseQuery, ISystemSubtype, ISystemVertical } from "../interfaces";
import { ISystemAsset, ISystemStory } from "../interfaces/response.interfaces";
import { TMinimalAuthor } from "../types";
import { COMMON_RESOURCES, SUBTYPES, VALUES, VERTICALS } from "./constants";

slugify.extend({ "$": "usd", "€": "eur" });

/** HELPERS */
/**
 * Simple fuzzy search
 * See: https://github.com/bevacqua/fuzzysearch/tree/master
 */
export const fuzzySearch = (needle: string, haystack: string) => {
  const hLength = haystack.length;
  const nLength = needle.length;

  if (nLength > hLength) {
    return false;
  }

  if (nLength === hLength) {
    return needle === haystack;
  }

  // outer: for (let i = 0, j = 0; i < nLength; i++) {
  //   const nch = needle.charCodeAt(i)
  //   while (j < hLength) {
  //     if (haystack.charCodeAt(j++) === nch) {
  //       continue outer
  //     }
  //   }

  //   return false
  // }

  const matches = (needleChar: number, startIndex: number): boolean => {
    for (let j = startIndex; j < hLength; j++) {
      if (haystack.charCodeAt(j) === needleChar) {
        return true;
      }
    }
    return false;
  };

  for (let i = 0, j = 0; i < nLength; i++) {
    const nch = needle.charCodeAt(i);
    if (!matches(nch, j)) {
      return false;
    }
    j++;
  }

  return true;
};

export const unescapeHtml = (unsafe: string) => {
  if (typeof unsafe === "undefined" || !unsafe) return "";

  return unsafe
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#039;/g, "'");
};

export const parseHtmlText = (content: string) => {
  if (!content) return content;

  // Match anchor tags with regex
  const replaced = content.replace(
    /<a\s+(?:[^>]*?\s+)?href="([^"]*)"([^>]*)>/gi,
    (match, href, rest) => {
      // Check if href is from partner link
      const isPartner = isPartnerLink(href);

      // For non-partner links: enforce rel="nofollow" and target="_blank"
      if (!isPartner) {
        let newRest = rest;
        // Ensure rel contains nofollow
        if (/\brel="[^"]*"/i.test(newRest)) {
          newRest = newRest.replace(
            /rel="([^"]*)"/i,
            (_m: string, rel: string) => `rel="${rel} nofollow"`,
          );
        } else {
          newRest += ` rel="nofollow"`;
        }

        // Ensure target is _blank
        if (/\btarget="[^"]*"/i.test(newRest)) {
          // newRest = newRest.replace(/target="[^"]*"/i, 'target="_blank"');
        } else {
          newRest += ` target="_blank"`;
        }

        return `<a href="${href}"${newRest}>`;
      }

      return match;
    },
  );

  return replaced;
};

export const getWidthFromContent = (content: string) => {
  const widthRegex = /width="(\d+)"/;
  const match = widthRegex.exec(content ?? "");
  return match ? `${match[1]}px` : null;
};

export const generateStoryInfo = (
  story: Pick<ISystemStory, "content_elements"> | null,
): ISystemStory["story_info"] => {
  let words_count = 0;
  let assets_count = 0;
  let embeds_count = 0;

  if (story?.content_elements?.length === 0) {
    return {
      words_count,
      assets_count,
      embeds_count,
    };
  }

  (story?.content_elements || []).forEach((element) => {
    if (element.type === "text" && element.content) {
      const text = element?.content?.replace(/<[^>]+>/g, " ");
      words_count += text?.trim()?.split(/\s+/).length;
    }
    if (element.type === "image" || element.type === "video") {
      assets_count += 1;
    }
    if (element.type === "custom_embed") {
      embeds_count += 1;
    }
  });

  return {
    words_count,
    assets_count,
    embeds_count,
  };
};

export const moveItemInArray = <T>(
  arr: T[],
  fromIndex: number,
  direction: "up" | "down",
): T[] => {
  const newArr = [...arr];
  const toIndex = direction === "up" ? fromIndex - 1 : fromIndex + 1;

  // prevent out-of-bounds moves
  if (toIndex < 0 || toIndex >= arr.length) return arr;

  // swap the items
  [newArr[fromIndex], newArr[toIndex]] = [newArr[toIndex], newArr[fromIndex]];

  return newArr;
};

export const generateQueryCacheKey = (query: IBaseQuery) => {
  const { limit, skip, sort, ...rest } = query;

  const keys = [
    "limit",
    "skip",
    "sort",
    ...Object.keys(rest).sort((a, b) => a.localeCompare(b)),
  ];

  return keys.map((key) => `${key}:${query[key as keyof typeof query]}`).join("|");
};

/**
 * Sleeps for a specified duration.
 * @param ms - The duration in milliseconds to sleep.
 * @returns A promise that resolves after the specified duration.
 */
export const sleepExecution = (ms = 10_000) => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

/**
 * Removes leading and trailing slashes from a string and adds a trailing slash.
 */
export const getLastUrlSegment = (url: string) => {
  return url
    .split("/")
    .filter((p) => p !== "")
    .pop();
};

export const formatSearchRelativeDate = (date: Date, days: number) => {
  return new Date(
    date.setDate(date.getDate() - days),
  ).toISOString()
}

export const generateIdempotencyKey = () => {
  return Date.now().toString();
}

/** TRANSFORMERS */
export const groupBy = <T>(
  array: T[],
  cb: (item: T) => string,
): Record<string, T[]> => {
  return array.reduce(
    (acc, item) => {
      const groupKey = cb(item);

      if (!acc[groupKey]) {
        acc[groupKey] = [];
      }

      acc[groupKey].push(item);
      return acc;
    },
    {} as Record<string, T[]>,
  );
};

// https://stackoverflow.com/a/40382963
export const shortenText = (str: string, maxLen: number) => {
  if (str.length <= maxLen) return str;
  return str.substring(0, str.lastIndexOf(" ", maxLen)) + "...";
};

export const shortenLabel = (name = "", maxLength = 30) => {
  if (name.length < maxLength) {
    return name;
  }

  const names = name.split(" ");

  const [firstName, ...restOfNames] = names;

  if (restOfNames.length) {
    return `${firstName} ${restOfNames
      .filter((n) => n && n.length > 2)
      .map((n) => n.charAt(0))
      .join(". ")}.`;
  }

  return name;
};

export const stripTags = (text: string) => text.replace(/(<([^>]+)>)/gi, "");

/** Regex: remove any character that is NOT a Unicode letter, number, space or hyphen (so slugs keep e.g. 日本語). */
const SLUG_ALLOW_CHARS = /[^\p{L}\p{N}\s\-]/gu;

/**
 * Converts a string to a slug format.
 * Preserves Unicode letters and numbers (e.g. Japanese 日本語) by using
 * strict: false and a custom remove regex; locale is passed through for
 * transliteration of other scripts when desired.
 * @param str - The string to convert.
 * @param options - slugify options (locale, lower, trim, replacement, etc.). strict is forced to false and remove is set so Unicode letters/numbers are kept.
 * @returns A slugified version of the string.
 */
export const slugifyString = (
  str: string,
  options: Parameters<typeof slugify>[1] = {
    locale: "en",
    lower: true,
    trim: true,
  },
) => {
  const opts = typeof options === "string" ? { replacement: options } : options ?? {};
  return slugify(str, {
    ...opts,
    strict: false,
    remove: SLUG_ALLOW_CHARS,
  });
};

export const dateToUTC = (date: Date): string => {
  return new Date(
    date.getTime() - date.getTimezoneOffset() * 60000,
  ).toISOString();
};

export const simplifyErrorLog = (error: any): string => {
  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === "string") {
    return error;
  }

  try {
    return JSON.stringify(error);
  } catch {
    return String(error);
  }
};

export const removeTrailingSlashFromUrl = (url: string) => {
  return url.replace(/\/$/, "");
};

export const ensureTrailingSlashOnUrl = (url: string): string => {
  if (!url) {
    return "/";
  }

  return url.endsWith("/") ? url : `${url}/`;
};

export const ensureProtocolOnUrl = (url: string, protocol: "http://" | "https://"): string => {
  if (!url) {
    return "";
  }

  if (url.startsWith(protocol)) {
    return url;
  }

  return `${protocol}${url}`;
}

/** EXTRACTORS */

export const generateStoryLink = (
  // story: Partial<
  //   Pick<ISystemStory, "slug" | "subtype" | "sub_vertical" | "primary_vertical">
  // > | null,
  story: {
    slug?: string;
    url?: string;
    subtypeObject?: Pick<ISystemSubtype, "slug"> | null;
    primaryVerticalObject?: Pick<ISystemVertical, "slug"> | null;
    sub_vertical?: string;
  } | null,
  options: {
    type: "use_url" | "use_segments";
    domain?: {
      add: boolean;
      url: string;
    };
    slug?: {
      add: boolean;
    };
  } = {
      type: "use_segments",
      domain: { add: false, url: "" },
      slug: { add: true },
    },
) => {
  let link = "";

  if (!story) {
    return "/";
  }

  if (options.type === "use_url" && story.url) {
    return ensureTrailingSlashOnUrl(story.url);
  }

  if (options?.domain?.add) {
    link += options?.domain?.url || "/";
  }

  switch (story?.subtypeObject?.slug) {
    case SUBTYPES.NEWS.SLUG: {
      link += `/articles/${story?.primaryVerticalObject?.slug}`;
      break;
    }

    case SUBTYPES.RESEARCH.SLUG: {
      // link += `/research`;

      // if (story?.sub_vertical === "external") {
      //   link += `/external`;
      // }

      switch (story.sub_vertical) {
        case "internal":
          link += `/research/internal`;
          break;
        case "external":
          link += `/external`;
          break;
        default:
          break;
      }

      break;
    }

    default:
      break;
  }

  if (options?.slug?.add) {
    link += `/${story?.slug || ""}`;
  }

  return ensureTrailingSlashOnUrl(link);
};

export const getAssetType = (
  input: File | string,
): ISystemAsset["mime_type"] => {
  // Case 1: File object (from input[type=file])
  if (input instanceof File) {
    const mime = input.type;

    if (mime.startsWith("image/")) return "image";
    if (mime.startsWith("video/")) return "video";

    return "file";
  }

  // Case 2: URL (string)
  if (typeof input === "string") {
    try {
      const url = new URL(input);
      const pathname = url.pathname.toLowerCase();

      if (/\.(jpg|jpeg|png|gif|webp|svg|avif)$/.test(pathname)) return "image";
      if (/\.(mp4|webm|ogg|mov|avi|mkv)$/.test(pathname)) return "video";

      return "file";
    } catch {
      // Not a valid URL, fallback
      return "file";
    }
  }

  return "file";
};

export const isAssetUsed = (asset: Pick<ISystemAsset,
  "users_images_aggregate" |
  "events_images_aggregate" |
  "reports_images_aggregate" |
  "main_asset_story_aggregate" |
  "homepage_asset_story_aggregate" |
  "social_media_asset_story_aggregate"
>) => {
  if (!asset) return false;

  const isUsed =
    // Usaged in users collection
    (asset.users_images_aggregate || 0)
    // Usaged in events collection
    + (asset.events_images_aggregate || 0)
    // Usaged in reports collection
    + (asset.reports_images_aggregate || 0)
    // Usaged in stories collection as main asset
    + (asset.main_asset_story_aggregate || 0)
    // Usaged in stories collection as homepage asset
    + (asset.homepage_asset_story_aggregate || 0)
    // Usaged in stories collection as social media asset
    + (asset.social_media_asset_story_aggregate || 0)
    > 0;

  return isUsed;
}

export const getStoryAssetUrl = (
  story: ISystemStory | null,
  assetType:
    | "mainAssetObject"
    | "homepageAssetObject"
    | "socialMediaAssetObject",
) => {
  if (!story) {
    return "";
  }

  if (story?.savedStoryVersionObject?.[assetType]) {
    return (
      story?.savedStoryVersionObject?.[assetType]?.src_url ||
      story?.savedStoryVersionObject?.mainAssetObject?.src_url
    );
  }

  return story?.[assetType]?.src_url || story?.mainAssetObject?.src_url;
};

export const getStoryVerticals = (story: ISystemStory, maxLength?: number) => {
  let verticals = story?.verticalsObjects || [];

  if (maxLength) {
    verticals = verticals.slice(0, maxLength);
  }

  return verticals;
};

export const getCategoryFromResearchStory = (story: ISystemStory): string | undefined => {
  const verticals = getStoryVerticals(story)
  const slugs = verticals.map(v => v.slug)

  if (slugs.includes(VERTICALS.INTRODUCING.SLUG)) return 'Introducing'
  if (slugs.includes(VERTICALS.INTERVIEWS.SLUG)) return 'Interviews'
  if (slugs.includes(VERTICALS.REPORTS.SLUG)) return 'Reports'

  // Anything else we treat as insights by default
  return 'Insights'
}

export const generateUserName = (author: TMinimalAuthor) => {
  return author.name || `${author.first_name} ${author.last_name}`;
};

export const getHostname = (url: string) => {
  try {
    const _url = new URL(url);
    return _url.hostname;
  } catch (error) {
    if (error instanceof TypeError) {
      return url;
    }
  }
};

export const getAssetSrcUrl = (asset: ISystemAsset | string) => {
  return typeof asset === "string" ? asset : asset.src_url;
};

/** VALIDATORS */
export const isValidUrl = (url: string): boolean => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

export const isValidHtml = (html: string): boolean => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const parserError = doc.querySelector("parsererror");
  return !parserError;
};

export const isExternalLink = (url: string) => {
  const hostname = getHostname(url);
  return (
    hostname &&
    !hostname.includes("dlnews.com") &&
    !hostname.includes("localhost") &&
    !hostname.includes("arc-cdn.net")
  );
};

export const isPartnerLink = (url: string) => {
  // if (url?.startsWith("/")) {
  //   return true;
  // }

  const hostname = getHostname(url);

  const partnerDomains = [
    "www.dlnews.com",
    "llama-corp.com",
    "defillama.com",
    "llamanodes.com",
    "llamafolio.com",
    "chainlist.org",
    "llamapay.io",
    "assets.dlnews.com",
  ];

  return hostname && partnerDomains.includes(hostname);
};

export const containsHTML = (str: string) => /<[a-z][\s\S]*>/i.test(str);

export const getUsedRoute = (route: string = "") => {
  const usedRoute = route.trim().toLowerCase().replace("/", "");

  switch (usedRoute) {
    case "editor":
      return COMMON_RESOURCES.STORIES.ROUTE;

    default:
      return usedRoute;
  }
};

export const checkRouteRequiredRole = (
  route: string,
  action: "c" | "r" | "u" | "d",
  permissions: string[] = [],
): boolean => {
  if (route === VALUES.EMPTY_FIELD) return true;

  if (permissions.length === 0) return false;

  const usedRoute = getUsedRoute(route);

  const permission = permissions.find((p) => p.startsWith(`${usedRoute}:`));
  if (!permission) return false;

  const [_resource, actions] = permission.split(":");

  if (!actions) return false;

  return actions.includes(action);
};
